/* eslint-disable no-console */
import React, { useState, useEffect } from 'react';
import get from 'lodash.get';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  List,
  DescriptionTable,
  JsonView,
  IconPopupOpen,
  Button,
  Notification,
  IconDelete,
  IconEdit,
  ConfirmationPopup,
} from 'shared/components';
import {
  displayValue,
  capitalizeFirstLetter,
  formatDate,
  hasPermission,
} from 'shared/helpers';
import { productFeatureTypes, defaultDateFormat } from 'shared/constants';
import LicenseFeatureDevices from './LicenseFeatureDevices';
import AddProductFeatureForm from './AddProductFeatureForm';
import ProductFeaturesEditorForm from 'CreateOrderForm/components/SelectProductForm/components/ProductFeaturesEditor/ProductFeaturesEditorForm';
import {
  updateLicenseProductFeature,
  deleteLicenseProductFeature,
  fetchLicenseProductFeatures,
} from 'licenses/actions';
import './styles.scss';

const PlatformFeatures = ({ license, refetchLicense }) => {
  const { t } = useTranslation();
  const licenseID = get(license, 'id');
  const canManageProductFeatures = hasPermission(
    'edit_license_product_features',
  );
  const [tableExpanded, setTableExpanded] = useState({});
  const [currentSort, setCurrentSort] = useState([
    { desc: false, id: 'product_feature__name' },
  ]);
  const [currentPage, setCurrentPage] = useState(0);
  const [refetchCounter, setRefetchCounter] = useState(0);
  const [showDevicesPopup, setDevicesPopupDisplay] = useState(false);
  const [devicePopupFeature, setDevicePopupFeature] = useState(null);
  const [featureToEdit, setFeatureToEdit] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [featureToDelete, setFeatureToDelete] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [list, setList] = useState([]);
  const [count, setCount] = useState(0);

  const tablePageSize = 20;

  useEffect(() => {
    if (!licenseID) return;
    setIsLoading(true);
    fetchLicenseProductFeatures(licenseID, {
      limit: tablePageSize,
      offset: currentPage * tablePageSize,
      orderBy: currentSort,
    })
      .then((response) => {
        setList(get(response, 'data.results', []));
        setCount(get(response, 'data.count', 0));
      })
      .catch((error) => {
        Notification(
          'error',
          t('Error occured'),
          t('There was an error while getting license features'),
        );
        console.error('Error fetching license features:', error);
      })
      .finally(() => setIsLoading(false));
  }, [licenseID, currentSort, currentPage, refetchCounter]);

  const refetchLicenseAndList = () => {
    if (typeof refetchLicense === 'function') {
      refetchLicense();
    }
    setRefetchCounter((prev) => prev + 1);
  };

  const handleSortChange = (newSort) => setCurrentSort(newSort);

  const handleFeatureEditClick = (rowData) => {
    const feature = get(rowData, 'original');
    setFeatureToEdit(feature);
  };

  const handleFeatureEdit = async (updatedFeature) => {
    try {
      const featureId = get(updatedFeature, 'id');

      // Prepare payload with only the fields that can be updated on the backend
      const payload = {
        max_consumption: updatedFeature.max_consumption,
        expiry_date: updatedFeature.expiry_date,
        allow_overages: updatedFeature.allow_overages,
        consumption_period: updatedFeature.consumption_period,
        max_overages: updatedFeature.max_overages,
        reset_consumption: updatedFeature.reset_consumption,
        allow_unlimited_consumptions:
          updatedFeature.allow_unlimited_consumptions,
        metadata: updatedFeature.metadata,
        floating_timeout: updatedFeature.floating_timeout,
        floating_users: updatedFeature.floating_users,
        is_floating: updatedFeature.is_floating,
        is_floating_cloud: updatedFeature.is_floating_cloud,
        allow_negative_consumptions: updatedFeature.allow_negative_consumptions,
        valid_duration: updatedFeature.valid_duration,
        can_borrow: updatedFeature.can_borrow,
        max_borrow_time: updatedFeature.max_borrow_time,
      };

      await updateLicenseProductFeature(featureId, payload);

      Notification(
        'success',
        t('Success'),
        t('License product feature updated successfully'),
      );

      refetchLicenseAndList();
      setFeatureToEdit(null);
    } catch (error) {
      Notification(
        'error',
        t('Error occurred'),
        t('There was an error while updating the license feature'),
      );
      console.error('Error updating license feature:', error);
    }
  };

  const handleFeatureDeleteClick = (rowData) => {
    const feature = get(rowData, 'original');
    setFeatureToDelete(feature);
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    if (!featureToDelete) return;

    try {
      const featureId = get(featureToDelete, 'id');

      await deleteLicenseProductFeature(featureId);

      Notification(
        'success',
        t('Success'),
        t('License product feature deleted successfully'),
      );

      refetchLicenseAndList();
      setShowDeleteConfirmation(false);
      setFeatureToDelete(null);
    } catch (error) {
      Notification(
        'error',
        t('Error occurred'),
        t('There was an error while deleting the license feature'),
      );
      console.error('Error deleting license feature:', error);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setFeatureToDelete(null);
  };

  const renderTotalConsumptions = (cellData) => {
    const type = get(cellData, 'original.product_feature.feature_type');
    if (type === productFeatureTypes.activation) return t('N/A');
    const value = get(cellData, 'value', 0);
    const maxConsumptions = get(cellData, 'original.max_consumption');
    const allowOverage = get(cellData, 'original.allow_overages');
    const maxOverages = get(cellData, 'original.max_overages');
    const isUnlimited = get(cellData, 'original.allow_unlimited_consumptions');
    if (isUnlimited) return `${value} / ${t('Unlimited')}`;
    if (allowOverage) {
      const maxValue = Number(maxConsumptions) + Number(maxOverages);
      return `${value} / ${maxValue}`;
    }
    return `${value} / ${maxConsumptions}`;
  };

  const renderFloatingUsers = (cellData) => {
    const isFloatingFeature =
      get(cellData, 'original.is_floating') ||
      get(cellData, 'original.is_floating_cloud');
    if (!isFloatingFeature) return 'N/A';

    const maxUsers = get(cellData, 'value');
    const featureDevices = get(cellData, 'original.device_features', []);
    const floatingDevicesInUse = featureDevices.reduce((acc, item) => {
      if (item.floating_in_use === true) return acc + 1;
      return acc;
    }, 0);
    return (
      <div>
        <span>{`${floatingDevicesInUse} / ${maxUsers}`}</span>
      </div>
    );
  };

  const renderDevices = (cellData) => {
    const featureDevices = get(cellData, 'original.device_features', []);
    return (
      <Button
        className="table-button"
        onClick={() => {
          setDevicePopupFeature(get(cellData, 'original'));
          setDevicesPopupDisplay(true);
        }}
        type="button"
      >
        <div className="floating-slots-btn">
          <span>{`${featureDevices.length}`}</span>
          <IconPopupOpen
            fill="none"
            stroke="#000"
            strokeWidth="2"
            width="18"
            height="18"
            viewBox="0 0 24 24"
          />
        </div>
      </Button>
    );
  };

  const columns = [
    {
      expander: true,
      Header: t('Details'),
      headerClassName: 'text-center',
      width: 80,
      style: {
        fontSize: 25,
        padding: '0',
        textAlign: 'center',
        userSelect: 'none',
      },
    },
    {
      accessor: 'product_feature.name',
      Header: t('Name'),
    },
    {
      accessor: 'product_feature.code',
      Header: t('Code'),
      sortable: true,
      width: 100,
    },
    {
      accessor: 'product_feature.feature_type',
      Header: t('Type'),
      Cell: (cellData) => displayValue(cellData.value),
      width: 100,
    },
    {
      accessor: 'total_consumptions',
      Header: t('Total consumptions'),
      sortable: true,
      className: 'text-center',
      headerClassName: 'text-center',
      Cell: renderTotalConsumptions,
    },
    {
      accessor: 'floating_users',
      Header: t('Floating slots'),
      className: 'text-center',
      headerClassName: 'text-center',
      Cell: renderFloatingUsers,
      maxWidth: 120,
    },
    {
      accessor: 'device_features',
      Header: t('Devices'),
      className: 'text-center',
      headerClassName: 'text-center',
      Cell: renderDevices,
      sortable: false,
    },
    {
      accessor: 'product_feature.created_at',
      Header: t('Created on'),
      Cell: (cellData) => formatDate(cellData.value, defaultDateFormat),
      maxWidth: 95,
    },
    {
      accessor: 'product_feature.expiry_date',
      Header: t('Expiry date'),
      Cell: (cellData) => formatDate(cellData.value, defaultDateFormat),
      width: 120,
    },
    ...(canManageProductFeatures
      ? [
          {
            id: 'edit',
            Cell: (rowData) => (
              <Button
                className="edit-button"
                onClick={() => handleFeatureEditClick(rowData)}
                type="button"
              >
                <IconEdit height="16px" width="16px" />
              </Button>
            ),
            maxWidth: 50,
            sortable: false,
          },
          {
            id: 'delete',
            Cell: (rowData) => (
              <Button
                className="edit-button"
                onClick={() => handleFeatureDeleteClick(rowData)}
                type="button"
              >
                <IconDelete height="16px" width="16px" color="#ee5253" />
              </Button>
            ),
            maxWidth: 50,
            sortable: false,
          },
        ]
      : []),
  ];

  return (
    <div className="ProductFeatures">
      <div className="ProductFeatures-header" style={{ marginBottom: '20px' }}>
        {canManageProductFeatures && (
          <Button
            theme="info"
            size="sm"
            onClick={() => setShowAddForm(true)}
            type="button"
          >
            {t('Add product feature')}
          </Button>
        )}
      </div>
      <List
        onExpandedChange={(expanded) => setTableExpanded(expanded)}
        expanded={tableExpanded}
        SubComponent={(row) => {
          const isFloatingFeature =
            get(row, 'original.is_floating') ||
            get(row, 'original.is_floating_cloud');
          const floatingTimeout = get(row, 'original.floating_timeout', 120);
          const isConsumption =
            get(row, 'original.product_feature.feature_type') === 'consumption';
          const allowOverages = get(row, 'original.allow_overages');
          const resetConsumption = get(row, 'original.reset_consumption');

          return (
            <div className="SubComponent">
              <DescriptionTable
                details={[
                  // consumption data
                  {
                    label: isConsumption ? t('Max consumptions') : null,
                    value: displayValue(get(row, 'original.max_consumption')),
                  },
                  {
                    label: isConsumption
                      ? t('Allow negative consumptions')
                      : null,
                    value: get(row, 'original.allow_negative_consumptions')
                      ? t('Yes')
                      : t('No'),
                  },
                  {
                    label:
                      isConsumption && allowOverages ? t('Max overages') : null,
                    value: displayValue(get(row, 'original.max_overages')),
                  },
                  {
                    label:
                      isConsumption && resetConsumption
                        ? t('Reset consumptions')
                        : null,
                    value: displayValue(
                      capitalizeFirstLetter(
                        get(row, 'original.consumption_period'),
                      ),
                      t('N/A'),
                    ),
                  },
                  // floating licenses data
                  {
                    label: get(row, 'original.is_floating')
                      ? t('Offline floating feature')
                      : null,
                    value: get(row, 'original.is_floating')
                      ? t('Yes')
                      : t('No'),
                  },
                  {
                    label: get(row, 'original.is_floating_cloud')
                      ? t('Is floating cloud')
                      : null,
                    value: get(row, 'original.is_floating_cloud')
                      ? t('Yes')
                      : t('No'),
                  },
                  {
                    label: isFloatingFeature
                      ? t('Max simultaneous license users')
                      : null,
                    value: displayValue(get(row, 'original.floating_users')),
                  },
                  {
                    label: isFloatingFeature ? t('Floating timeout') : null,
                    value: `${displayValue(floatingTimeout)} min`,
                  },
                  {
                    label: t('Metadata'),
                    value: (
                      <JsonView
                        value={get(row, 'original.metadata')}
                        name="metadata"
                      />
                    ),
                  },
                ]}
              />
            </div>
          );
        }}
        columns={columns}
        data={list || []}
        defaultPageSize={tablePageSize}
        minRows={2}
        loading={isLoading}
        clickable={false}
        manual
        onSortedChange={handleSortChange}
        defaultSorted={currentSort}
        page={currentPage}
        pages={Math.ceil(count / tablePageSize)}
        showPagination={count > tablePageSize}
        onPageChange={(page) => setCurrentPage(page)}
      />
      {showDevicesPopup && (
        <LicenseFeatureDevices
          feature={devicePopupFeature}
          close={() => {
            setDevicePopupFeature(null);
            setDevicesPopupDisplay(false);
          }}
          onFeatureDeviceRevoke={() => {
            refetchLicenseAndList();
            setDevicePopupFeature(null);
            setDevicesPopupDisplay(false);
          }}
        />
      )}
      {featureToEdit && (
        <ProductFeaturesEditorForm
          feature={featureToEdit}
          closeCb={() => setFeatureToEdit(null)}
          onConfirm={handleFeatureEdit}
          license={license}
          disableFeatureExpiry={false}
        />
      )}
      {showDeleteConfirmation && (
        <ConfirmationPopup
          title={t('Delete License Feature')}
          confirmText={t('Delete')}
          theme="error"
          warning
          confirmCb={handleConfirmDelete}
          closeCb={handleCancelDelete}
        >
          <p>
            {t('Are you sure you want to delete the feature')}{' '}
            <strong>{get(featureToDelete, 'product_feature.name')}</strong>?
          </p>
          <p>{t('This action cannot be undone.')}</p>
        </ConfirmationPopup>
      )}
      {showAddForm && (
        <AddProductFeatureForm
          closeCb={() => setShowAddForm(false)}
          refetchLicense={refetchLicenseAndList}
          license={license}
        />
      )}
    </div>
  );
};

PlatformFeatures.propTypes = {
  license: PropTypes.object.isRequired,
  refetchLicense: PropTypes.func.isRequired,
};

export default PlatformFeatures;
